import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { CopyIcon } from '@phosphor-icons/react/dist/csr/Copy';
import { FilePdfIcon } from '@phosphor-icons/react/dist/csr/FilePdf';

import { Button } from '../../design-system/primitives/Button/Button';
import styles from './RecoveryCodeList.module.css';

/**
 * The ten codes, shown once.
 *
 * "Once" is literal and it is a server property, not a UI convention: the API
 * stores SHA-256 digests, so nothing — no reload, no support call, no database
 * query — can produce these strings again. A user who leaves this screen
 * without saving them can only regenerate the set, which invalidates the one
 * they were just given. That is why the warning is a `role="alert"` region and
 * not a caption, and why leaving takes an explicit acknowledgement rather than
 * a close button in a corner.
 *
 * ## Copy and print, and why both
 *
 * Copying is the path an agent will actually take; printing is the one that
 * survives the workstation being reimaged. `navigator.clipboard` is neither
 * universal nor unconditional — it needs a secure context and a user gesture,
 * and it is absent in jsdom — so the failure is handled and told to the user
 * rather than swallowed into a button that appears to do nothing.
 */

export interface RecoveryCodeListProps {
  codes: readonly string[];
  /** The user has said they saved them. The parent decides what follows. */
  onAcknowledge: () => void;
}

export function RecoveryCodeList({ codes, onAcknowledge }: RecoveryCodeListProps) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState<string | null>(null);

  async function copyAll(): Promise<void> {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setNotice(t('mfa.copied'));
    } catch {
      // Not swallowed: a copy button that silently did nothing would leave the
      // agent believing they had saved credentials they have not saved.
      setNotice(t('mfa.copyFailed'));
    }
  }

  return (
    <section className={styles['panel']} aria-labelledby="recoveryCodesTitle">
      <h3 className={styles['title']} id="recoveryCodesTitle">
        {t('mfa.codesTitle')}
      </h3>

      {/*
        `role="alert"` and not a caption. This is the only moment these strings
        exist, and a screen-reader user who is never told that has lost them by
        the time they find out.
      */}
      <p className={styles['warning']} role="alert">
        {t('mfa.codesWarning')}
      </p>

      {/*
        A list, so assistive technology announces "10 items" and the user can
        step through them one at a time. `dir="ltr"` because a recovery code is
        a code identifier and must not mirror on an Arabic page.
      */}
      <ul className={styles['codes']} aria-label={t('mfa.codesList')} data-testid="recoveryCodes">
        {codes.map((code) => (
          <li key={code} className={styles['code']}>
            <bdi dir="ltr">{code}</bdi>
          </li>
        ))}
      </ul>

      <div className={styles['actions']}>
        <Button
          icon={<CopyIcon size={16} />}
          variant="secondary"
          onClick={() => {
            void copyAll();
          }}
        >
          {t('mfa.copyCodes')}
        </Button>

        <Button
          icon={<FilePdfIcon size={16} />}
          variant="secondary"
          onClick={() => {
            window.print();
          }}
        >
          {t('mfa.printCodes')}
        </Button>

        <Button icon={<CheckIcon size={16} />} onClick={onAcknowledge}>{t('mfa.codesDone')}</Button>
      </div>

      {/*
        Mounted from the first frame, empty — the copy result arrives later, and
        a region inserted with its text already in it gives assistive technology
        nothing to diff.
      */}
      <div className={styles['notice']} role="status" aria-atomic="true" data-testid="copyNotice">
        {notice}
      </div>
    </section>
  );
}
